/**
 * Video Reviews Carousel Component
 * CSS-based animation with drag/swipe support
 */

// Global flag to track if videos have been unlocked by user gesture
let videosUnlocked = false;

function unlockVideos() {
  if (videosUnlocked) return;
  videosUnlocked = true;

  // Unlock autoplay for in-view carousels + lazy background videos (same gesture)
  document
    .querySelectorAll('video-reviews-carousel video, video-background-component[data-play-on-visible] video')
    .forEach((video) => {
    video.muted = true;
    video.setAttribute('muted', '');
    
    // Play and immediately pause to unlock the video context
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.then(() => {
        // Video is now unlocked, pause if not visible
        if (!isElementInViewport(video)) {
          video.pause();
        }
      }).catch(() => {});
    }
  });

  // Remove listeners after first interaction
  document.removeEventListener('touchstart', unlockVideos, true);
  document.removeEventListener('touchend', unlockVideos, true);
  document.removeEventListener('click', unlockVideos, true);
}

function isElementInViewport(el) {
  const rect = el.getBoundingClientRect();
  return (
    rect.top < window.innerHeight &&
    rect.bottom > 0 &&
    rect.left < window.innerWidth &&
    rect.right > 0
  );
}

// Listen for first user interaction to unlock videos
document.addEventListener('touchstart', unlockVideos, { capture: true, passive: true });
document.addEventListener('touchend', unlockVideos, { capture: true, passive: true });
document.addEventListener('click', unlockVideos, { capture: true, passive: true });

class VideoReviewsCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.slides = null;
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.dragOffset = 0;
    this.contentWidth = 0;
    this.speed = parseFloat(this.dataset.speed) || 30;
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';
  }

  connectedCallback() {
    this.track = this.querySelector('[ref="track"]');
    this.slides = this.querySelector('[ref="slides"]');

    if (!this.track || !this.slides) return;

    // Wait for content to render
    requestAnimationFrame(() => {
      this.init();
    });
  }

  init() {
    // Clone slides for infinite scroll
    this.cloneSlides();

    // Calculate content width and set animation
    this.contentWidth = this.slides.offsetWidth;
    const gap = parseFloat(getComputedStyle(this).getPropertyValue('--video-reviews-gap')) || 20;
    this.contentWidth += gap;

    // Set animation duration based on width and speed
    const duration = this.contentWidth / this.speed;
    this.style.setProperty('--video-reviews-duration', `${duration}s`);
    this.style.setProperty('--video-reviews-width', `-${this.contentWidth}px`);

    // Setup video observer
    this.setupVideoObserver();

    // Setup drag
    this.setupDrag();

    // Setup hover pause
    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => {
        if (!this.isDragging) this.resume();
      });
    }
  }

  cloneSlides() {
    const clone = this.slides.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    this.track.appendChild(clone);
  }

  setupVideoObserver() {
    const videos = this.querySelectorAll('video');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        if (entry.isIntersecting) {
          // Ensure required attributes for Safari autoplay
          video.muted = true;
          video.setAttribute('muted', '');
          video.setAttribute('playsinline', '');
          
          // Try to play with promise handling for Safari
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.catch(() => {
              // If autoplay fails, show poster and try again on user interaction
              video.load();
            });
          }
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.3 });

    videos.forEach(video => {
      // Set all required attributes for autoplay
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      observer.observe(video);
    });
  }

  setupDrag() {
    // Mouse events
    this.track.addEventListener('mousedown', (e) => this.onDragStart(e));
    window.addEventListener('mousemove', (e) => this.onDragMove(e));
    window.addEventListener('mouseup', () => this.onDragEnd());

    // Touch events
    this.track.addEventListener('touchstart', (e) => this.onDragStart(e), { passive: true });
    window.addEventListener('touchmove', (e) => this.onDragMove(e), { passive: true });
    window.addEventListener('touchend', () => this.onDragEnd());
  }

  onDragStart(e) {
    this.isDragging = true;
    this.pause();

    // Get current animated position
    const style = getComputedStyle(this.track);
    const matrix = new DOMMatrix(style.transform);
    this.currentX = matrix.m41;

    // Stop CSS animation and apply current position
    this.track.style.animation = 'none';
    this.track.style.transform = `translateX(${this.currentX}px)`;

    this.startX = e.type.includes('mouse') ? e.pageX : e.touches[0].pageX;
    this.dragOffset = 0;
    this.track.style.cursor = 'grabbing';
  }

  onDragMove(e) {
    if (!this.isDragging) return;

    const x = e.type.includes('mouse') ? e.pageX : e.touches[0].pageX;
    this.dragOffset = x - this.startX;

    let newX = this.currentX + this.dragOffset;

    // Loop around
    if (newX > 0) {
      newX = newX - this.contentWidth;
    } else if (newX < -this.contentWidth) {
      newX = newX + this.contentWidth;
    }

    this.track.style.transform = `translateX(${newX}px)`;
  }

  onDragEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.track.style.cursor = 'grab';

    // Get final position
    const style = getComputedStyle(this.track);
    const matrix = new DOMMatrix(style.transform);
    let finalX = matrix.m41;

    // Normalize position
    while (finalX > 0) finalX -= this.contentWidth;
    while (finalX < -this.contentWidth) finalX += this.contentWidth;

    // Calculate animation delay to resume from current position
    const progress = Math.abs(finalX) / this.contentWidth;
    const duration = this.contentWidth / this.speed;
    const delay = -(progress * duration);

    // Resume CSS animation from current position
    this.track.style.transform = '';
    this.track.style.animation = '';
    this.style.setProperty('--video-reviews-delay', `${delay}s`);

    setTimeout(() => this.resume(), 100);
  }

  pause() {
    this.style.setProperty('--video-reviews-status', 'paused');
  }

  resume() {
    this.style.setProperty('--video-reviews-status', 'running');
  }
}

if (!customElements.get('video-reviews-carousel')) {
  customElements.define('video-reviews-carousel', VideoReviewsCarousel);
}
